import React, { useEffect } from "react";
import styles from "../BusinessAttributes.module.css";
import TextArea from "uicomponents/TextArea";
import RadioInput from "uicomponents/RadioInput";
import { Alert } from "@justworkshr/milo-core";
import TextInput from "uicomponents/TextInput";
import DocumentUpload from "uicomponents/DocumentUpload";
import { useFormikContext } from "formik";
import { sectionsQuestions } from "pages/WorkersCompApplcation/utils/sectionsQuestions";
import { BusinessAttributesFormType } from "../../../utils/formType";
import { WCAppType } from "types";

interface IBusinessAttributesComponentProps {
  data?: WCAppType;
  fieldRefs: React.MutableRefObject<{ [key: string]: HTMLInputElement | null }>;
}

const BusinessAttributesComponent = ({
  ...props
}: IBusinessAttributesComponentProps): JSX.Element => {
  const { data, fieldRefs } = props;
  const { values, setFieldValue } = useFormikContext();
  const hasPreviousCarriers = (values as BusinessAttributesFormType)
    ?.hasPriorCarriers;
  const hasHadWorkersCompPolicy = (values as BusinessAttributesFormType)
    ?.hasHadWorkersCompPolicy;
  const hasBeenCanceled = (values as BusinessAttributesFormType)?.beenCancelled;
  const hasThirdPartyVendors = (values as BusinessAttributesFormType)
    ?.use3rdPartyVendors;
  const hasVolunteers = (values as BusinessAttributesFormType)?.hasVolunteers;
  const hasVolunteerAccidentPolicy = (values as BusinessAttributesFormType)
    ?.hasVolunteerAccidentPolicy;
  const hasHazardousMats = (values as BusinessAttributesFormType)
    ?.hazardousMats;

  const labelList = sectionsQuestions["businessAttributes"].questions;
  const messageList = sectionsQuestions["businessAttributes"].message;

  useEffect(() => {
    if (hasPreviousCarriers === "No") {
      const updateFieldList = ["priorCarriers", "hasHadWorkersCompPolicy"];
      updateFieldList.forEach((field) => {
        setFieldValue(field, "");
      });
    }
  }, [hasPreviousCarriers, setFieldValue]);

  useEffect(() => {
    if (hasBeenCanceled === "No") {
      setFieldValue("policyCancelledReason", "");
    }
  }, [hasBeenCanceled, setFieldValue]);

  useEffect(() => {
    if (hasThirdPartyVendors === "No") {
      const updateFieldList = [
        "thirdPartyDetails1",
        "thirdPartyDetails2",
        "thirdPartyDetails3",
        "thirdPartyDetails4",
      ];
      updateFieldList.forEach((field) => {
        setFieldValue(field, "");
      });
    }
  }, [hasThirdPartyVendors, setFieldValue]);

  useEffect(() => {
    if (hasVolunteers === "No") {
      const updateFieldList = [
        "numberVolunteers",
        "volunteerActivities",
        "hasVolunteerAccidentPolicy",
        "volunteerTravel",
        "volunteerAccidentPolicy",
      ];
      updateFieldList.forEach((field) => {
        setFieldValue(field, "");
      });
    }
  }, [hasVolunteers, setFieldValue]);

  return (
    <div>
      <div className={styles.title}>
        {sectionsQuestions["businessAttributes"].title}
      </div>
      <div className={styles.description}>
        {sectionsQuestions["businessAttributes"].description}
      </div>
      <RadioInput
        name="hasPriorCarriers"
        label={labelList.hasPriorCarriers}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasPreviousCarriers === "Yes" && (
        <>
          <TextArea
            name="priorCarriers"
            label={labelList.priorCarriers}
            fieldRefs={fieldRefs}
          />
          <RadioInput
            name="hasHadWorkersCompPolicy"
            options={["Yes", "No"]}
            label={labelList.hasHadWorkersCompPolicy}
            fieldRefs={fieldRefs}
          />
          {hasHadWorkersCompPolicy === "No" && (
            <Alert
              color="warning"
              className={styles.hasHadWorkersCompPolicyAlert}
            >
              By selecting “No” you attest that you are not aware of any workers
              compensation claims in the last 5 years.
            </Alert>
          )}
        </>
      )}
      <RadioInput
        name="beenCancelled"
        label={labelList.beenCancelled}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasBeenCanceled === "Yes" && (
        <TextArea
          name="policyCancelledReason"
          label={labelList.policyCancelledReason}
          fieldRefs={fieldRefs}
        />
      )}
      <RadioInput
        name="hasPastDuePremiums"
        label={labelList.hasPastDuePremiums}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      <RadioInput
        name="beenDeclined"
        label={labelList.beenDeclined}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      <RadioInput
        name="sponsorSportsTeams"
        label={labelList.sponsorSportsTeams}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      <RadioInput
        name="hasTaxLeinBankrupcy"
        label={labelList.hasTaxLeinBankrupcy}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      <RadioInput
        name="offerHealthInsurance"
        label={labelList.offerHealthInsurance}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      <RadioInput
        name="ownAircraftWatercraft"
        label={labelList.ownAircraftWatercraft}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      <RadioInput
        name="use3rdPartyVendors"
        label={labelList.use3rdPartyVendors}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />

      {hasThirdPartyVendors === "Yes" && (
        <>
          <TextArea
            name="thirdPartyDetails1"
            label={labelList.thirdPartyDetails1}
            message={messageList.thirdPartyDetails1}
            fieldRefs={fieldRefs}
          />
          <TextArea
            name="thirdPartyDetails2"
            label={labelList.thirdPartyDetails2}
            fieldRefs={fieldRefs}
          />
          <TextArea
            name="thirdPartyDetails3"
            label={labelList.thirdPartyDetails3}
            fieldRefs={fieldRefs}
          />
          <RadioInput
            name="thirdPartyDetails4"
            label={labelList.thirdPartyDetails4}
            options={["Yes", "No"]}
            fieldRefs={fieldRefs}
          />
        </>
      )}

      <RadioInput
        name="hasVolunteers"
        label={labelList.hasVolunteers}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasVolunteers === "Yes" && (
        <>
          <TextInput
            name="numberVolunteers"
            label={labelList.numberVolunteers}
            fieldRefs={fieldRefs}
          />
          <TextArea
            name="volunteerActivities"
            label={labelList.volunteerActivities}
            fieldRefs={fieldRefs}
          />
          <RadioInput
            name="hasVolunteerAccidentPolicy"
            label={labelList.hasVolunteerAccidentPolicy}
            options={["Yes", "No"]}
            fieldRefs={fieldRefs}
          />
          {hasVolunteerAccidentPolicy === "Yes" && (
            <DocumentUpload
              name="volunteerAccidentPolicy"
              label={labelList.volunteerAccidentPolicy}
              application={data}
              fieldRefs={fieldRefs}
            />
          )}
          <RadioInput
            name="volunteerTravel"
            label={labelList.volunteerTravel}
            options={["Yes", "No"]}
            fieldRefs={fieldRefs}
          />
        </>
      )}
      <RadioInput
        name="hazardousMats"
        label={labelList.hazardousMats}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasHazardousMats === "Yes" && (
        <DocumentUpload
          name="materialSafetyDataSheets"
          label={labelList.materialSafetyDataSheets}
          required={false}
          application={data}
        />
      )}
      <RadioInput
        name="hasWrittenSafteyProg"
        label={labelList.hasWrittenSafteyProg}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
    </div>
  );
};

export default BusinessAttributesComponent;
